import { Country } from "@daytrip/legacy-models";
import { Vehicle } from "@daytrip/legacy-models";
import { VehicleMake } from "@daytrip/legacy-models";
import { VehicleModel } from "@daytrip/legacy-models";
import { isVehicleAllowedOldByCountry } from "@daytrip/utils";
import { action, computed, observable } from "mobx";

import { DocumentTypeOperator } from "./DocumentTypeOperator"; // change inport
import { ModelOperator } from "./ModelOperator";
import { ModelOperatorOptions } from "./ModelOperatorOptions";

interface VehicleOperatorOptions extends ModelOperatorOptions<Vehicle, null, VehicleOperatorData> {
    documentTypes?: Array<DocumentTypeOperator>;
    country?: Country;
}

interface VehicleOperatorData {
    vehicleMake?: VehicleMake;
    vehicleModel?: VehicleModel;
}

interface VehicleOperatorDataFetched {
    vehicleMake: VehicleMake;
    vehicleModel: VehicleModel;
}

const VEHICLE_MAX_AGE = 10;
const VEHICLE_MAX_AGE_OLD = 15;

export class VehicleOperator extends ModelOperator<
    Vehicle,
    VehicleOperatorOptions,
    null,
    VehicleOperatorData,
    VehicleOperatorDataFetched
> {
    @observable
    public vehicleMake?: VehicleMake;

    @observable
    public vehicleModel?: VehicleModel;

    @observable
    public documentTypeOperators?: Array<DocumentTypeOperator>;

    @observable
    public isNew = false;

    @observable
    private country?: Country;

    public constructor(options: VehicleOperatorOptions) {
        super(options);

        this.vehicleMake = this.data.vehicleMake;
        this.vehicleModel = this.data.vehicleModel;
        this.documentTypeOperators = options.documentTypes;
        this.country = options.country;
    }

    @computed
    public get maxVehicleAge(): number {
        if (this.m.allowOld) {
            return VEHICLE_MAX_AGE_OLD;
        }

        const vehicleSeatsCount = this.vehicleModel?.seatsCount;

        if (!this.country || !vehicleSeatsCount) {
            return VEHICLE_MAX_AGE;
        }

        const allowOldByCountry = isVehicleAllowedOldByCountry(this.country.isoCode, vehicleSeatsCount);

        return allowOldByCountry ? VEHICLE_MAX_AGE_OLD : VEHICLE_MAX_AGE;
    }

    @computed
    public get minVehicleYear(): number {
        const currentYear = new Date().getFullYear();
        const maxVehicleAge = this.maxVehicleAge;

        return currentYear - maxVehicleAge;
    }

    @action.bound
    public setVehicleMake(make: VehicleMake) {
        this.vehicleMake = make;
    }

    @action.bound
    public setVehicleModel(model: VehicleModel | undefined) {
        this.vehicleModel = model;
    }

    @action.bound
    public async approveVehicle(): Promise<void> {
        await this.rpcClient.vehicle.approveVehicle(this.m._id);
        this.model = await this.rpcClient.vehicle.retrieveVehicle(this.m._id);
    }

    @observable
    public objectDeclinationReason: string;

    @action.bound
    public changeVehicleDeclinationReason(value: string): void {
        this.objectDeclinationReason = value;
    }

    @action.bound
    public async declineVehicle(): Promise<void> {
        try {
            await this.rpcClient.vehicle.declineVehicle(this.m._id, this.objectDeclinationReason);
            this.model = await this.rpcClient.vehicle.retrieveVehicle(this.m._id);
            this.objectDeclinationReason = "";
        } catch (e: any) {
            alert("Not declined, contact admin.");
        }
    }

    @action.bound
    public async deleteVehicle(): Promise<void> {
        try {
            await this.rpcClient.vehicle.deleteVehicle(this.m._id);
            this.edit((o) => (o.deletedAt = new Date()));
        } catch (e: any) {
            alert("not deleted, contact admin.");
        }
    }

    @action.bound
    public async setAllowOld(allowOld: boolean): Promise<void> {
        this.edit((v) => {
            v.allowOld = allowOld;
            if (!allowOld && v.manufactureYear < this.minVehicleYear) {
                // not sure what the autor meant here
                v.manufactureYear = null as unknown as number;
            }
        });
    }
}
